import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useCurrentStore } from '../../context/CurrentStoreContext'
import { Card, CardHeader } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Alert } from '../../components/ui/Alert'
import { SkeletonCard } from '../../components/ui/Skeleton'
import { StatusModal, useStatusModal } from '../../components/ui/StatusModal'
import * as categoryService from '../../services/categoryService'
import * as homepageSectionsService from '../../services/homepageSectionsService'
import { MAX_SECTION_PRODUCTS } from '../../services/homepageSectionsService'
import { fetchProducts } from '../../services/productService'
import { formatMoney } from '@/lib/money'
import { getUserFacingErrorMessage } from '../../utils/userFacingError'

const CATALOG_PAGE_SIZE = 12
const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23374151" width="64" height="64"/%3E%3C/svg%3E'

export type CatalogProductRow = {
  id: number
  name: string
  slug: string
  sku?: string
  price?: number
  imageUrl?: string
}

function rowFromItem(item: unknown): CatalogProductRow | null {
  if (!item || typeof item !== 'object') return null
  const o = item as Record<string, unknown>
  const id = typeof o.id === 'number' ? o.id : Number(o.id)
  if (!Number.isFinite(id)) return null
  const price = typeof o.price === 'number' ? o.price : Number(o.price)
  const images = Array.isArray(o.images) ? o.images : []
  const firstImage = images[0]
  return {
    id,
    name: String(o.name ?? ''),
    slug: String(o.slug ?? ''),
    sku: o.sku != null ? String(o.sku) : undefined,
    price: Number.isFinite(price) ? price : undefined,
    imageUrl: typeof firstImage === 'string' && firstImage.trim() ? firstImage : undefined,
  }
}

function statusBadgeClass(status: homepageSectionsService.VisibilityStatus): string {
  switch (status) {
    case 'active_now':
      return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
    case 'scheduled':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-300'
    case 'expired':
      return 'bg-muted text-muted-foreground'
    case 'disabled':
      return 'bg-destructive/15 text-destructive'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

function productIdsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false
  return a.every((id, i) => id === b[i])
}

function ProductThumb({ url, name }: { url?: string; name: string }) {
  return (
    <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-muted">
      <img
        src={url || PLACEHOLDER_IMAGE}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
        onError={(e) => {
          const img = e.currentTarget
          if (img.src !== PLACEHOLDER_IMAGE) img.src = PLACEHOLDER_IMAGE
        }}
      />
      <span className="sr-only">{name}</span>
    </div>
  )
}

export default function HomepageSectionProductsPage() {
  const navigate = useNavigate()
  const { sectionId: sectionIdParam } = useParams()
  const { currentStoreId: storeId } = useCurrentStore()

  const sectionId = sectionIdParam != null ? Number(sectionIdParam) : NaN
  const idsValid = Number.isFinite(sectionId) && sectionId >= 1 && storeId != null && storeId >= 1

  const { openSuccess, openError, statusModalProps } = useStatusModal()

  const [section, setSection] = useState<homepageSectionsService.HomepageSectionRecord | null>(null)
  const [sectionLoading, setSectionLoading] = useState(true)
  const [sectionError, setSectionError] = useState<string | null>(null)

  const [selected, setSelected] = useState<CatalogProductRow[]>([])
  const [initialIds, setInitialIds] = useState<number[]>([])
  const [saving, setSaving] = useState(false)

  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [categorySlug, setCategorySlug] = useState('')
  const [categories, setCategories] = useState<categoryService.CategoryRow[]>([])
  const [catalogPage, setCatalogPage] = useState(1)
  const [catalogRows, setCatalogRows] = useState<CatalogProductRow[]>([])
  const [catalogTotal, setCatalogTotal] = useState(0)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const selectedSet = useMemo(() => new Set(selected.map((p) => p.id)), [selected])
  const dirty = !productIdsEqual(
    selected.map((p) => p.id),
    initialIds,
  )
  const atMax = selected.length >= MAX_SECTION_PRODUCTS
  const remaining = Math.max(0, MAX_SECTION_PRODUCTS - selected.length)

  const skipQResetRef = useRef(true)
  useEffect(() => {
    const t = window.setTimeout(() => setQ(searchInput.trim()), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => {
    if (skipQResetRef.current) {
      skipQResetRef.current = false
      return
    }
    setCatalogPage(1)
  }, [q, categorySlug])

  const hydrateSelected = useCallback(async (ids: number[]) => {
    if (ids.length === 0) {
      setSelected([])
      setInitialIds([])
      return
    }
    const res = await fetchProducts({ page: 1, limit: 100, isActive: true })
    const byId = new Map<number, CatalogProductRow>()
    for (const item of res.items) {
      const row = rowFromItem(item)
      if (row) byId.set(row.id, row)
    }
    const ordered = ids.map((id) => byId.get(id) ?? { id, name: `Product #${id}`, slug: '' })
    setSelected(ordered)
    setInitialIds([...ids])
  }, [])

  const loadSection = useCallback(async () => {
    if (!idsValid) return
    setSectionLoading(true)
    setSectionError(null)
    try {
      const row = await homepageSectionsService.getSection(storeId, sectionId)
      setSection(row)
      if (row.sourceType === 'manual_products') {
        await hydrateSelected(row.manualProductIds ?? [])
      }
    } catch (e) {
      setSection(null)
      setSectionError(getUserFacingErrorMessage(e, 'Could not load section'))
    } finally {
      setSectionLoading(false)
    }
  }, [idsValid, storeId, sectionId, hydrateSelected])

  useEffect(() => {
    void loadSection()
  }, [loadSection])

  useEffect(() => {
    categoryService
      .fetchCategories({ page: 1, limit: 100 })
      .then((res) => setCategories(res.items.filter((c) => c.source === 'store' && c.slug)))
      .catch(() => setCategories([]))
  }, [])

  useEffect(() => {
    if (!idsValid || section?.sourceType !== 'manual_products') return
    let cancelled = false
    setCatalogLoading(true)
    setCatalogError(null)
    fetchProducts({
      page: catalogPage,
      limit: CATALOG_PAGE_SIZE,
      q: q || undefined,
      categorySlug: categorySlug || undefined,
      isActive: true,
    })
      .then((res) => {
        if (cancelled) return
        const parsed = res.items.map(rowFromItem).filter(Boolean) as CatalogProductRow[]
        setCatalogRows(parsed)
        setCatalogTotal(res.total ?? parsed.length)
      })
      .catch((e) => {
        if (!cancelled) {
          setCatalogRows([])
          setCatalogError(getUserFacingErrorMessage(e, 'Could not load products'))
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [idsValid, section?.sourceType, catalogPage, q, categorySlug])

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  const addProduct = (row: CatalogProductRow) => {
    if (selectedSet.has(row.id) || atMax) return
    setSelected((prev) => [...prev, row])
  }

  const removeProduct = (id: number) => {
    setSelected((prev) => prev.filter((p) => p.id !== id))
  }

  const clearAll = () => {
    if (selected.length === 0) return
    if (!window.confirm('Remove all products from this section?')) return
    setSelected([])
  }

  const moveProduct = (index: number, direction: -1 | 1) => {
    const next = index + direction
    if (next < 0 || next >= selected.length) return
    setSelected((prev) => {
      const copy = [...prev]
      const tmp = copy[index]
      copy[index] = copy[next]
      copy[next] = tmp
      return copy
    })
  }

  const handleSave = async () => {
    if (!idsValid) return
    setSaving(true)
    try {
      const manualProducts = selected.map((p, i) => ({ productId: p.id, sortOrder: i }))
      const updated = await homepageSectionsService.updateSectionProducts(storeId, sectionId, manualProducts)
      setSection(updated)
      const ids = updated.manualProductIds ?? selected.map((p) => p.id)
      setInitialIds([...ids])
      openSuccess({
        title: 'Products saved',
        description: 'Homepage section products and order were updated.',
      })
    } catch (e) {
      openError({
        title: 'Save failed',
        description: getUserFacingErrorMessage(e, 'Could not save products'),
      })
    } finally {
      setSaving(false)
    }
  }

  const handleBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return
    navigate('/homepage-sections')
  }

  const catalogTotalPages = Math.max(1, Math.ceil(catalogTotal / CATALOG_PAGE_SIZE))
  const sectionTitle = section?.title?.trim() || section?.systemKey || 'Section'

  if (!idsValid) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">Invalid store or section.</Alert>
        <Link to="/homepage-sections">
          <Button type="button" variant="secondary">
            Back to sections
          </Button>
        </Link>
      </div>
    )
  }

  if (sectionLoading && !section) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading">
        <SkeletonCard className="h-28" />
        <div className="grid gap-6 lg:grid-cols-2">
          <SkeletonCard className="h-96" />
          <SkeletonCard className="h-96" />
        </div>
      </div>
    )
  }

  if (sectionError || !section) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">{sectionError ?? 'Section not found'}</Alert>
        <Link to="/homepage-sections">
          <Button type="button" variant="secondary">
            Back to sections
          </Button>
        </Link>
      </div>
    )
  }

  if (section.sourceType !== 'manual_products') {
    return (
      <div className="space-y-6">
        <nav className="text-xs text-muted-foreground">
          <Link to="/homepage-sections" className="text-primary hover:underline">
            Homepage Sections
          </Link>
          <span className="mx-1.5">/</span>
          <span>{sectionTitle}</span>
          <span className="mx-1.5">/</span>
          <span>Manage Products</span>
        </nav>
        <Card>
          <CardHeader title="Automatic product source" />
          <p className="px-6 pb-6 text-sm text-muted-foreground">
            This section uses an automatic product source. Product selection is managed by its source type, not
            manual picks.
          </p>
          <div className="px-6 pb-6">
            <Link to="/homepage-sections">
              <Button type="button" variant="secondary">
                Back to sections
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <StatusModal {...statusModalProps} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 space-y-2">
          <nav className="text-xs text-muted-foreground">
            <Link to="/homepage-sections" className="text-primary hover:underline">
              Homepage Sections
            </Link>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">{sectionTitle}</span>
            <span className="mx-1.5">/</span>
            <span>Manage Products</span>
          </nav>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Manage Products</h1>
          <p className="text-sm text-muted-foreground">Add or remove products from this homepage section.</p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={handleBack}>
            Back to sections
          </Button>
          <Button type="button" disabled={!dirty || saving} onClick={() => void handleSave()}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">{sectionTitle}</h2>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(section.visibilityStatus)}`}
              >
                {section.visibilityStatus.replace('_', ' ')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {section.sectionType.replace('_', ' ')} · {section.sourceType.replace('_', ' ')} · Max{' '}
              {MAX_SECTION_PRODUCTS} products
            </p>
          </div>
          <div className="flex gap-6 border-t border-border pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
            <div>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{selected.length}</p>
              <p className="text-xs text-muted-foreground">Products</p>
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-foreground">
                {selected.length} / {MAX_SECTION_PRODUCTS}
              </p>
              <p className="text-xs text-muted-foreground">Selected</p>
            </div>
            <div>
              <p className="text-2xl font-semibold tabular-nums text-foreground">{remaining}</p>
              <p className="text-xs text-muted-foreground">Remaining</p>
            </div>
          </div>
        </div>
        {atMax ? (
          <div className="border-t border-amber-500/30 bg-amber-500/10 px-6 py-3 text-sm text-amber-800 dark:text-amber-200">
            You&apos;ve reached the maximum number of products for this section.
          </div>
        ) : null}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Add products" description="Search your catalog and add products to this section." />
          <div className="space-y-4 px-6 pb-6">
            <Input
              label="Search"
              placeholder="Search products by name, SKU, slug…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              autoComplete="off"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategorySlug('')}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  categorySlug === ''
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  type="button"
                  onClick={() => setCategorySlug(c.slug)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    categorySlug === c.slug
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
            {catalogError ? <Alert variant="destructive">{catalogError}</Alert> : null}
            {catalogLoading ? (
              <div className="space-y-3" aria-busy="true">
                {Array.from({ length: 4 }).map((_, i) => (
                  <SkeletonCard key={i} className="h-16" />
                ))}
              </div>
            ) : catalogRows.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                No products match your search.
              </p>
            ) : (
              <ul className="space-y-2">
                {catalogRows.map((row) => {
                  const added = selectedSet.has(row.id)
                  return (
                    <li
                      key={row.id}
                      className="flex items-center gap-3 rounded-xl border border-border bg-card p-3"
                    >
                      <ProductThumb url={row.imageUrl} name={row.name} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{row.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {row.sku ? `SKU: ${row.sku}` : row.slug}
                        </p>
                        {row.price != null ? (
                          <p className="mt-0.5 text-sm tabular-nums text-foreground">{formatMoney(row.price)}</p>
                        ) : null}
                      </div>
                      {added ? (
                        <span className="shrink-0 rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
                          Added
                        </span>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          className="shrink-0"
                          disabled={atMax}
                          onClick={() => addProduct(row)}
                        >
                          Add
                        </Button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
            {catalogTotalPages > 1 ? (
              <div className="flex items-center justify-between gap-2 pt-2 text-sm">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={catalogPage <= 1 || catalogLoading}
                  onClick={() => setCatalogPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {catalogPage} of {catalogTotalPages}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={catalogPage >= catalogTotalPages || catalogLoading}
                  onClick={() => setCatalogPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            ) : null}
          </div>
        </Card>

        <Card>
          <CardHeader
            title={`Selected products (${selected.length})`}
            description="Use the arrows to reorder. Order matches the homepage display."
            action={
              selected.length > 0 ? (
                <Button type="button" variant="secondary" size="sm" onClick={clearAll}>
                  Clear all
                </Button>
              ) : undefined
            }
          />
          <div className="px-6 pb-6">
            {selected.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
                No products yet. Search the catalog on the left and add products to this section.
              </p>
            ) : (
              <ul className="space-y-2">
                {selected.map((row, index) => (
                  <li
                    key={row.id}
                    className="flex items-center gap-2 rounded-xl border border-border bg-card p-3"
                  >
                    <span className="w-5 shrink-0 text-center text-xs text-muted-foreground" aria-hidden>
                      ⋮⋮
                    </span>
                    <span className="w-6 shrink-0 text-sm font-medium tabular-nums text-muted-foreground">
                      {index + 1}
                    </span>
                    <ProductThumb url={row.imageUrl} name={row.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{row.name}</p>
                      {row.price != null ? (
                        <p className="text-sm tabular-nums text-muted-foreground">{formatMoney(row.price)}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={index === 0}
                        onClick={() => moveProduct(index, -1)}
                        aria-label="Move up"
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={index === selected.length - 1}
                        onClick={() => moveProduct(index, 1)}
                        aria-label="Move down"
                      >
                        ↓
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => removeProduct(row.id)}>
                        Remove
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}
